import { ElementType, FC, ReactNode } from "react";

import { PunchupTag } from "@punchup/ui-common/punchup/primitives/display/PunchupTag";
import { cx } from "../../../utils/cx";
import styles from "./PunchupConversionCard.module.css";

export type PunchupConversionCardTone = "primary-soft" | "cream" | "ink";

export interface PunchupConversionCardProps {
  /**
   * Background treatment. `primary-soft` (default) — the warm orange tint for
   * recurring in-feed prompts; `cream` — the quiet canvas tone (bordered) for
   * rail cards on white; `ink` — the Stage-Black panel for a loud end-of-feed
   * or sign-up moment. One `ink` card per viewport-full of content, and never
   * adjacent to a `PunchupHeroCard` (two dark panels flatten each other).
   */
  tone?: PunchupConversionCardTone;
  /** Optional brand mark / logo slot above the heading. */
  mark?: ReactNode;
  /** Optional eyebrow chip above the heading, rendered as a display-mode PunchupTag. */
  eyebrow?: string;
  heading: ReactNode;
  /**
   * Heading element. Defaults to `h2` — conversion cards sit inside a page that
   * already owns its h1.
   */
  headingAs?: ElementType;
  /** One supporting line under the heading. */
  line?: ReactNode;
  /**
   * CTA row — `PunchupButton` actions (`asChild` + link for navigation). Keep to
   * one primary per card; the second action should be `secondary`/`outline`.
   */
  actions?: ReactNode;
  /**
   * Decorative media column (e.g. an overlapping avatar cluster of real
   * artists), laid out beside the copy. In-flow — it reserves its own space —
   * and `aria-hidden`: it is presentation, not content. Same contract as
   * `PunchupHeroCard`'s media slot.
   */
  media?: ReactNode;
  /**
   * Purely decorative background layer (e.g. a dimmed drifting face wall),
   * rendered absolutely under the copy: `aria-hidden`, `pointer-events: none`
   * — it can never block a CTA or reach assistive tech. Same contract as
   * `PunchupHeroCard`'s decorative slot; the caller owns any scrim.
   */
  decorative?: ReactNode;
  className?: string;
}

/**
 * PunchupConversionCard — the one conversion surface for logged-out prompts:
 * the sticky rail sign-up card, the recurring in-feed join card, and the
 * end-of-feed redirect all compose this with different slots + tone.
 */
export const PunchupConversionCard: FC<PunchupConversionCardProps> = ({
  tone = "primary-soft",
  mark,
  eyebrow,
  heading,
  headingAs: Heading = "h2",
  line,
  actions,
  media,
  decorative,
  className,
}) => {
  const toneClass = {
    "primary-soft": styles.tonePrimarySoft,
    cream: styles.toneCream,
    ink: styles.toneInk,
  }[tone];

  return (
    <section className={cx(styles.card, toneClass, className)}>
      {decorative && (
        <div className={styles.decorative} aria-hidden="true">
          {decorative}
        </div>
      )}
      <div className={styles.inner}>
        <div className={styles.content}>
          {mark && <div className={styles.mark}>{mark}</div>}
          {eyebrow && (
            <div className={styles.eyebrow}>
              <PunchupTag label={eyebrow} />
            </div>
          )}
          <Heading className={styles.heading}>{heading}</Heading>
          {line && (
            <div
              className={cx(styles.line, tone === "ink" && styles.lineInverse)}
            >
              {line}
            </div>
          )}
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
        {media && (
          <div className={styles.media} aria-hidden="true">
            {media}
          </div>
        )}
      </div>
    </section>
  );
};
